import { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { EXPORT_HEADERS, buildExportRows, commentaryExportFilename } from './CommentaryTrackerExportUtils';

/**
 * "Export to Excel" button for the Commentary Tracker.
 *
 * Mirrors the look of the Analytics AnalyticsExportButton (icon-only, react-csv CSVLink), but the
 * export needs per-portfolio detail for EVERY filtered company — which is loaded lazily on screen.
 * So on click it fills any missing detail (reusing the page's rowDetails cache), flattens to rows,
 * then clicks the hidden CSVLink once the rows are in state (the standard react-csv async pattern).
 */
const CommentaryTrackerExportButton = ({ companies, quarter, rowDetails, fetchDetail, loading }) => {
	const linkRef = useRef();
	const pendingDownload = useRef(false);
	const [building, setBuilding] = useState(false);
	const [rows, setRows] = useState([]);

	const disabled = loading || building || !companies || companies.length === 0;

	// Once the freshly-built rows land in state, trigger the hidden download link.
	useEffect(() => {
		if (pendingDownload.current && rows.length > 0 && linkRef.current && linkRef.current.link) {
			linkRef.current.link.click();
			pendingDownload.current = false;
			setBuilding(false);
		}
	}, [rows]);

	const handleClick = async () => {
		if (disabled) return;
		setBuilding(true);
		try {
			const detailsById = { ...(rowDetails || {}) };
			const missing = companies.filter(c => !detailsById[c.externalAccessId]);
			const fetched = await Promise.all(
				missing.map(c =>
					fetchDetail(c.externalAccessId)
						.then(detail => ({ id: c.externalAccessId, detail }))
						// A failed detail fetch shouldn't abort the whole export — emit company-only row.
						.catch(() => ({ id: c.externalAccessId, detail: { portfolios: [] } }))
				)
			);
			fetched.forEach(({ id, detail }) => { detailsById[id] = detail; });
			pendingDownload.current = true;
			setRows(buildExportRows(companies, quarter, detailsById));
		} catch (e) {
			pendingDownload.current = false;
			setBuilding(false);
		}
	};

	return (
		<>
			<button
				type="button"
				onClick={handleClick}
				disabled={disabled}
				aria-label="Export to Excel"
				title={building ? 'Exporting…' : 'Export to Excel'}
				style={{
					background: 'transparent',
					border: 'none',
					padding: '0.25rem',
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					cursor: disabled ? 'not-allowed' : 'pointer',
					opacity: disabled ? 0.4 : 1,
					color: 'inherit',
				}}
			>
				<span className="material-symbols-outlined" style={{ fontSize: 18, lineHeight: 1 }}>file_export</span>
			</button>
			<CSVLink
				ref={linkRef}
				headers={EXPORT_HEADERS}
				data={rows}
				filename={commentaryExportFilename(quarter)}
				style={{ display: 'none' }}
				target="_self"
			/>
		</>
	);
};

export default CommentaryTrackerExportButton;
